import React, { useEffect, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { Select, Typography, Tooltip, Card } from "antd";
import Spin from "Components/Spinner";
import { formatCurrency, createPricingOptions, sumPurchaseProdutcs } from "../../../utils/helper";
import useMediaQuery from "use-media-antd-query";
import ExternalManagement from "../ExternalManagementInfo";
import OtherProducts from "../OtherProducts";
import { totalPaymentTooltip, totalPayment, totalPriceInstallmentsInfo, getOfferData } from "../helper";
import CustomButton from "Components/CustomButton";
const { Text } = Typography;

const Pricing = ({ productData, data, actionHandler, isMobile, customStyle }) => {
  const { t } = useTranslation();
  const colSize = useMediaQuery();
  const [statusAction, setStatusAction] = useState({});
  const [viewDetailedPrices, setViewDetailedPrices] = useState(false);
  const selectedOffer = data?.price_option;
  const purchaseProducts = data?.purchase_products;

  const mainProduct = purchaseProducts.find((product) => product.type === "main");
  const otherProducts = purchaseProducts.filter((product) => product.type === "bump");

  const isExternalManagement = productData?.management === "external";

  useEffect(() => {
    setStatusAction(data?.status?.action);
  }, [data]);

  if (!productData || !data) {
    return <Spin />;
  }

  const grandTotal = sumPurchaseProdutcs(data?.purchase_products);
  const mainProductTotal = sumPurchaseProdutcs(data?.purchase_products, true);
  const hasCoupon = data?.coupon?.active === true;

  const InstallmentsFrequency = ({ offer, data, hasBumps, hasCoupon }) => {
    const product = data?.purchase_products?.find((product) => product.type === "main");
    const interval = offer?.payments_interval;

    if (interval !== 1 && interval !== null && interval !== undefined) {
      const frequency = product.payments_frequency === "month" ? t("pricing.frequencyMonths") : t("pricing.frequencyYears");

      return (
        <div>
          <div>
            <div>{formatCurrency(mainProductTotal?.price)} {t("pricing.nextInstalment")}</div>
          </div>
          <div>
            {t("pricing.instalmentEvery", { interval, frequency })}
          </div>
        </div>
      );
    }

    // Fallback
    const frequency = product.payments_frequency === "month" ? t("pricing.frequencyMonthly") : t("pricing.frequencyYearly");

    return (
      <div>
        {hasBumps ? <div className="small">{formatCurrency(mainProductTotal?.price)} {t("pricing.nextInstalment")}</div> : <span>{t("pricing.instalmentFrequency", { frequency })}</span>}
      </div>
    );
  };

  const RecurrenceFrequency = ({ offer, data, hasBumps, hasCoupon }) => {
    const product = data?.purchase_products?.find((product) => product.type === "main");

    const interval = offer?.recurrence_billing_method;
    const installments = offer?.recurrence_max_payments;
    const missingInstallments = installments - 1;

    const frequency =
      product.payments_interval === 1
        ? product.payments_frequency === "month"
          ? t("pricing.frequencyMonthly")
          : t("pricing.frequencyYearly")
        : t("pricing.recurrenceEvery", {
            interval: product.payments_interval,
            frequency: product.payments_frequency === "month" ? t("pricing.frequencyMonths") : t("pricing.frequencyYears"),
          });

    if (hasCoupon === true) {
      return (
        <div>
          <div>
            <div>{t("pricing.recurrenceLabel", { frequency })}</div>
          </div>
        </div>
      );
    }

    return (
      <div>
        <div>
          <div>
            {t("pricing.recurrenceAmount", { amount: formatCurrency(mainProductTotal?.price), frequency })}
          </div>
        </div>
      </div>
    );
  };

  const PriceInfo = ({ offer, data }) => {
    const hasBumps = data.bumps?.length > 0;

    // **********************************************************************************************************
    // RECURRENCE WITHOUT BUMPS
    // **********************************************************************************************************
    if (offer?.price_method === "recurrence" && hasBumps === false) {
      return (
        <div>
          <div className="price">
            <div>{t("pricing.totalToPay")}</div>
            <div>
              <div style={{ textAlign: "right" }}>{formatCurrency(grandTotal?.price)}</div>
            </div>
          </div>
          <div className="installments-info">
            <RecurrenceFrequency offer={offer} data={data} hasBumps={hasBumps} hasCoupon={hasCoupon} />
          </div>
        </div>
      );
    }

    // **********************************************************************************************************
    // RECURRENCE WITH BUMPS
    // **********************************************************************************************************
    if (offer?.price_method === "recurrence" && hasBumps === true) {
      return (
        <div>
          <div className="price">
            <div>{t("pricing.totalToPay")}</div>
            <div>
              <div style={{ textAlign: "right" }}>{formatCurrency(grandTotal?.price)} {t("pricing.today")}</div>
            </div>
          </div>
          <div className="installments-info">
            <RecurrenceFrequency offer={offer} data={data} hasBumps={hasBumps} hasCoupon={hasCoupon} />
          </div>
        </div>
      );
    }

    // **********************************************************************************************************
    // INSTALLMENTS WITHOUT BUMPS
    // **********************************************************************************************************
    if (offer?.price_method === "installments" && hasBumps === false) {
      return (
        <div>
          <div className="price">
            <div>{t("pricing.totalToPay")}</div>
            <div>
              <div style={{ textAlign: "right" }}>
                {hasCoupon === true ? (
                  <span>{formatCurrency(grandTotal?.price)}</span>
                ) : (
                  <span>
                    {offer.n_payments} x {formatCurrency(grandTotal?.price)}
                  </span>
                )}
              </div>
            </div>
          </div>
          <div className="installments-info">
            <InstallmentsFrequency offer={offer} data={data} hasBumps={hasBumps} hasCoupon={hasCoupon} />
          </div>
        </div>
      );
    }

    // **********************************************************************************************************
    // INSTALLMENTS WITH BUMPS
    // **********************************************************************************************************
    if (offer?.price_method === "installments" && hasBumps === true) {
      return (
        <div>
          <div className="price">
            <div>{t("pricing.totalToPay")}</div>
            <div>
              <div style={{ textAlign: "right" }}>{formatCurrency(grandTotal?.price)} {t("pricing.today")}</div>
            </div>
          </div>
          <div className="installments-info">
            <InstallmentsFrequency offer={offer} data={data} hasBumps={hasBumps} hasCoupon={hasCoupon} />
          </div>
        </div>
      );
    }

    // Fallback
    return (
      <div>
        <div className="price">
          <div>{t("pricing.totalToPay")} </div>
          <div>
            {totalPriceInstallmentsInfo(mainProduct, otherProducts)} {formatCurrency(grandTotal?.price)}
          </div>
        </div>
      </div>
    );
  };

  const CouponInfo = ({ data, selectedOffer }) => {
    if (!data || !selectedOffer) {
      return null;
    }
    const coupon = data?.coupon;

    if (!coupon || coupon?.active !== true) {
      return null;
    }
    const couponUses = coupon?.installments_uses || -1;
    const maxInstallments = selectedOffer.price_method === "installments" ? selectedOffer.n_payments : selectedOffer.recurrence_max_payments;
    const couponInstallmentsUses = couponUses > maxInstallments ? -1 : couponUses;
    const installmentsType = selectedOffer.price_method === "installments" ? t("pricing.installmentsType") : t("pricing.recurrencesType");

    if (couponInstallmentsUses === -1) {
      return (
        <Tooltip title={t("pricing.couponApplicableAll")}>
          <span>
            <small>
              <em className="text-muted">
                {t("pricing.applicableAllInstallments", { count: maxInstallments, type: installmentsType })}
              </em>
            </small>
          </span>
        </Tooltip>
      );
    }

    if (couponInstallmentsUses > 0) {
      const applyToText =
        couponInstallmentsUses === 1 ? (
          <span>
            <Trans i18nKey="pricing.couponApplyToSingular" components={{ strong: <strong /> }} />
          </span>
        ) : (
          <span>
            <Trans i18nKey="pricing.couponApplyToPlural" values={{ count: couponInstallmentsUses, type: installmentsType }} components={{ strong: <strong /> }} />
          </span>
        );

      return (
        <Tooltip title={<>{t("pricing.couponIsApplicable")} {applyToText}</>}>
          <small>
            <em className="text-muted">{t("pricing.applicable")} {applyToText}</em>
          </small>
        </Tooltip>
      );
    }
  };

  if (data?.is_teaser === true) {
    return (
      <div className="pricing-container">
        <div className="text-center">{isMobile ? <h3>{t("pricing.freeAccess")}</h3> : <h2>{t("pricing.freeAccess")}</h2>}</div>
      </div>
    );
  }

  return (
    <div
      className="pricing-container"
      style={customStyle?.primary?.backgroundColor ? { "--price-accent": customStyle.primary.backgroundColor } : undefined}
    >
      {productData?.offers.length > 1 && (
        <div className="price-selector">
          <Select
            disabled={
              statusAction === "prepare-payment" || statusAction === "wait-for-payment" || statusAction === "checkout" || statusAction === "paid" ? true : false
            }
            showSearch={false}
            value={data?.price_option?.offer_id}
            className="price-select"
            placeholder={t("pricing.selectPaymentPlaceholder")}
            onChange={(e) => actionHandler("price_option", getOfferData(e, productData))}
          >
            {createPricingOptions(productData?.offers, t)}
          </Select>
        </div>
      )}
      <div className="pricing-summary-panel">
        {otherProducts?.length > 0 && (
          <OtherProducts
            mainProduct={mainProduct}
            otherProducts={otherProducts}
            colSize={colSize}
            selectedOffer={selectedOffer}
            viewDetailedPrices={viewDetailedPrices}
            isExternalManagement={isExternalManagement}
            hasCoupon={hasCoupon}
            coupon={data?.coupon}
          />
        )}
        <div className="grand-pricing">
        <Card className="grand-pricing-card">
          <PriceInfo offer={selectedOffer} data={data} />

          {/* Show only if product isnt external management */}

          <div className="grand-price-details">
            {data?.coupon?.active === true && (
              <>
                <div className="grand-price-detail">
                  <Text className="grand-price-detail-title">{t("pricing.originalPrice")}</Text>
                  <Text className="grand-price-detail-value">{formatCurrency(grandTotal?.full_price)}</Text>
                </div>
                <div className="grand-price-detail-discount">
                  <Text className="grand-price-detail-title-discount">{t("pricing.couponDiscount")}</Text>
                  <Text className="grand-price-detail-value-discount">
                    {"-"}
                    {formatCurrency(grandTotal?.discount)}
                  </Text>
                </div>
                {selectedOffer.price_method !== "one_time" && (
                  <div className="grand-price-detail-discount mb-1">
                    <CouponInfo data={data} selectedOffer={selectedOffer} />
                  </div>
                )}
              </>
            )}

            {isExternalManagement === false && isMobile === false && (
              <div className="grand-price-detail">
                <Text className="grand-price-detail-title">{t("pricing.priceExclVat")}</Text>
                <Text className="grand-price-detail-value">{formatCurrency(grandTotal?.price_wo_vat)}</Text>
              </div>
            )}

            {isExternalManagement === false && isMobile === false && (
              <div className="grand-price-detail">
                <Text className="grand-price-detail-title">{t("pricing.vatLabel", { vat: data?.geo?.region?.tax_percent })}</Text>
                <Text className="grand-price-detail-value">{formatCurrency(grandTotal?.vat_value)}</Text>
              </div>
            )}

            {/* {colSize !== "xs" && (
              <div className="grand-price-detail">
                <Text className="grand-price-detail-title">TOTAL A PAGAR:</Text>
                <Text className="grand-price-detail-value">{formatCurrency(grandTotal?.price)}</Text>
              </div>
            )} */}

            {selectedOffer.price_method === "installments" && selectedOffer?.n_payments > 1 && otherProducts?.length === 0 && (
              <div className="installments_disclaimer">
                <Tooltip title={totalPaymentTooltip(selectedOffer, data?.coupon, t)}>
                  <Text className="text">{t("pricing.instalmentDisclaimer", { total: totalPayment(selectedOffer, data?.coupon) })}</Text>
                </Tooltip>
              </div>
            )}

            {selectedOffer.price_method === "recurrence" &&
              selectedOffer?.recurrence_billing_method === "limited" &&
              selectedOffer?.recurrence_max_payments > 1 && (
                <div className="installments_disclaimer">
                  <Tooltip title={totalPaymentTooltip(selectedOffer, data?.coupon, t)}>
                    <Text className="text">{t("pricing.recurrenceMaxPayments", { max: selectedOffer?.recurrence_max_payments })}</Text>
                  </Tooltip>
                </div>
              )}
          </div>

          {/* Show if external management */}
          {isExternalManagement && <ExternalManagement productData={productData} grandTotal={grandTotal} />}

          {/* footer row: details toggle (left) + offer tag (right) */}
          {((!isExternalManagement && purchaseProducts?.length > 1) || selectedOffer?.public_name) && (
            <div className="pricing-footer-row">
              {!isExternalManagement && purchaseProducts?.length > 1 ? (
                <CustomButton buttonStyle={customStyle} type="link" style={{ margin: 0, padding: 0 }} onClick={() => setViewDetailedPrices((value) => !value)}>
                  <small>{!viewDetailedPrices ? t("pricing.viewDetails") : t("pricing.hideDetails")}</small>
                </CustomButton>
              ) : (
                <span />
              )}

              {selectedOffer?.public_name && (
                <div className="pricing-offer-name">
                  <small className="text-muted">{selectedOffer?.public_name}</small>
                </div>
              )}
            </div>
          )}
        </Card>
        </div>
      </div>
    </div>
  );
};

export default Pricing;
